import type { PopoverOrigin, SxProps, Theme } from '@mui/material';
import type { MouseEvent } from 'react';
import { getLabel } from '../../../utils/multiSelectUtils';
import { CHIP_GAP_PX, CHIP_MAX_WIDTH_PX, CHIP_SELECTOR, OVERFLOW_CHIP_RESERVE_PX } from '../constants';

const CONTAINER_SX_ALL_VISIBLE: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: `${CHIP_GAP_PX}px`,
    flexWrap: 'wrap',
    width: '100%',
    minWidth: 0
};

const CONTAINER_SX_OVERFLOW: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: `${CHIP_GAP_PX}px`,
    flexWrap: 'nowrap',
    overflow: 'hidden',
    width: '100%',
    minWidth: 0,
    flex: 1,
    position: 'relative'
};

const CHIP_ITEM_HIDDEN_SX: SxProps<Theme> = {
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none'
};

const CHIP_ITEM_SX_ALL_VISIBLE: SxProps<Theme> = {
    maxWidth: `min(${CHIP_MAX_WIDTH_PX}px, 100%)`,
    minWidth: 0,
    flexShrink: 1
};

const CHIP_ITEM_SX_ALL_VISIBLE_HIDDEN: SxProps<Theme> = {
    ...CHIP_ITEM_SX_ALL_VISIBLE,
    ...CHIP_ITEM_HIDDEN_SX
};

const CHIP_ITEM_SX_OVERFLOW: SxProps<Theme> = {
    maxWidth: CHIP_MAX_WIDTH_PX,
    minWidth: 0,
    flexShrink: 0
};

const CHIP_ITEM_SX_OVERFLOW_HIDDEN: SxProps<Theme> = {
    ...CHIP_ITEM_SX_OVERFLOW,
    ...CHIP_ITEM_HIDDEN_SX
};

const CHIP_LABEL_ELLIPSIS_SX: SxProps<Theme> = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block'
};

const VISIBLE_CHIP_SX_INTERACTIVE: SxProps<Theme> = {
    maxWidth: '100%',
    cursor: 'pointer',
    '& .MuiChip-label': CHIP_LABEL_ELLIPSIS_SX,
    '& .MuiChip-deleteIcon': {
        flexShrink: 0
    }
};

const VISIBLE_CHIP_SX_READ_ONLY: SxProps<Theme> = {
    ...VISIBLE_CHIP_SX_INTERACTIVE,
    cursor: 'default'
};

const OVERFLOW_CHIP_SX_INTERACTIVE: SxProps<Theme> = {
    maxWidth: 'fit-content',
    cursor: 'pointer'
};

const OVERFLOW_CHIP_SX_READ_ONLY: SxProps<Theme> = {
    maxWidth: 'fit-content',
    cursor: 'default'
};

export const OVERFLOW_MORE_CHIP_SX: SxProps<Theme> = {
    cursor: 'pointer',
    flexShrink: 0
};

export const OVERFLOW_POPOVER_CONTENT_SX: SxProps<Theme> = {
    p: 1,
    gap: 1
};

export const OVERFLOW_POPOVER_ANCHOR_ORIGIN: PopoverOrigin = {
    vertical: 'bottom',
    horizontal: 'left'
};

export const OVERFLOW_POPOVER_TRANSFORM_ORIGIN: PopoverOrigin = {
    vertical: 'top',
    horizontal: 'left'
};

export function stopPropagation(event: MouseEvent<HTMLElement>) {
    event.stopPropagation();
}

/**
 * How many chips fit in the container, leaving room for a `+N` chip when needed.
 * Hidden chips stay mounted with `visibility: hidden` so their widths remain measurable.
 */
export function countChipsThatFit(container: HTMLElement): number {
    const chips = container.querySelectorAll<HTMLElement>(CHIP_SELECTOR);
    const total = chips.length;
    const available = container.clientWidth;

    let used = 0;
    let count = 0;

    for (let i = 0; i < total; i += 1) {
        const width = chips[i].offsetWidth;
        const remaining = total - (i + 1);
        const needed =
            used + width + (count > 0 ? CHIP_GAP_PX : 0) + (remaining > 0 ? CHIP_GAP_PX + OVERFLOW_CHIP_RESERVE_PX : 0);

        if (needed > available && (count > 0 || remaining > 0)) {
            break;
        }

        used += width + (count > 0 ? CHIP_GAP_PX : 0);
        count += 1;
    }

    return count;
}

export function buildSelectedValuesKey(selectedValues: unknown[]): string {
    return selectedValues.map(getLabel).join('\0');
}

export function getOverflowValues(
    selectedValues: unknown[],
    visibleCount: number,
    allValuesVisible: boolean
): unknown[] {
    if (allValuesVisible) {
        return [];
    }
    return selectedValues.slice(visibleCount);
}

export function getContainerSx(allValuesVisible: boolean): SxProps<Theme> {
    return allValuesVisible ? CONTAINER_SX_ALL_VISIBLE : CONTAINER_SX_OVERFLOW;
}

export function getChipItemSx(allValuesVisible: boolean, isHidden: boolean): SxProps<Theme> {
    if (allValuesVisible) {
        return isHidden ? CHIP_ITEM_SX_ALL_VISIBLE_HIDDEN : CHIP_ITEM_SX_ALL_VISIBLE;
    }
    return isHidden ? CHIP_ITEM_SX_OVERFLOW_HIDDEN : CHIP_ITEM_SX_OVERFLOW;
}

export function getVisibleChipSx(readOnly: boolean): SxProps<Theme> {
    return readOnly ? VISIBLE_CHIP_SX_READ_ONLY : VISIBLE_CHIP_SX_INTERACTIVE;
}

export function getOverflowChipSx(readOnly: boolean): SxProps<Theme> {
    return readOnly ? OVERFLOW_CHIP_SX_READ_ONLY : OVERFLOW_CHIP_SX_INTERACTIVE;
}
